import { Fragment, useCallback, useState } from 'react';
import { useLoaderData, useRouteLoaderData } from '@remix-run/react';
import { type DehydratedState } from '@tanstack/react-query';
import { ClientOnly } from 'remix-utils/client-only';

import { REPUBS } from '@cx-modules/constants';

import Breadcrumbs from '~/components/Breadcrumbs';
import Filters from '~/components/Filters';
import Icon from '~/components/icon';
import Tabs, { type TabElement, tabStyles } from '~/components/Tabs';
import { OffersWidget } from '~/components/Widgets/Offers/Offers';
import { StandardLayout } from '~/layouts/StandardLayout';
import { OddsGrid } from '~/modules/OddsGrid/OddsGrid';
import { SubeventHeader } from '~/modules/SubeventHeader/SubeventHeader';
import type { loader as rootLoader } from '~/root-loader';
import { createSubeventJsonLd } from '~/utils/createSubeventJsonLd';

import type { loader } from './loader';

export { handle } from './handle';
export { loader } from './loader';
export { meta } from './meta';

export default function Subevent() {
	const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root')!;
	const data = useLoaderData<typeof loader>();
	const {
		DISABLE_GRID_TAB_STATS,
		DISABLE_OFFER_WIDGETS,
		cardMapping,
		componentState: { oddsGrids, subeventHeader },
		deviceType,
		geoLocationInfo,
		mapping,
		marketGroups,
		oddsFormat,
		projectionsUrl,
		repub,
		siteConfig,
		url,
	} = data;

	const renderSubeventHeader = () => {
		if (!subeventHeader) return null;
		return (
			<SubeventHeader
				dataApiEndpoint="/api"
				dehydratedState={subeventHeader as DehydratedState}
				deviceType={deviceType}
				repub={repub}
				subeventId={mapping.id}
			/>
		);
	};

	const tabElements = (() => {
		const usTabElements: TabElement[] = [
			{ name: 'Best Odds', type: 'Tab' },
			{ name: 'Offers', type: 'Tab' },
			{
				content: (
					<a className={tabStyles.linkWrapper} href={projectionsUrl}>
						<span className={tabStyles.icon}>
							<Icon
								accent="#1ed560"
								className={tabStyles.projection}
								icon="Projections"
								primary="#00182e"
							/>
						</span>
						<span className={tabStyles.text}>Stats</span>
					</a>
				),
				name: 'Stats',
				type: 'Link',
				disabled: DISABLE_GRID_TAB_STATS || !projectionsUrl,
			},
		];

		const ocTabElements: TabElement[] = [
			{ name: 'Best Odds', type: 'Tab' },
			{ name: 'Match Stats', type: 'Tab' },
			{ name: 'Offers', type: 'Tab' },
			{ name: 'Tips', type: 'Tab' },
		];

		const elements = repub === REPUBS.US.name ? usTabElements : ocTabElements;

		return elements.filter(el => !el.disabled);
	})();

	const [selectedTab, setSelectedTab] = useState<TabElement>(tabElements[0]);
	const handleTabChange = useCallback((tabElement: TabElement) => {
		if (tabElement.type === 'Link') return;

		setSelectedTab(tabElement);
	}, []);

	const marketGroupNames = Object.keys(marketGroups) as string[];

	const [selectedMarketGroup, setSelectedMarketGroup] = useState<string>(
		marketGroupNames[0],
	);
	const handleMarketChange = useCallback((marketGroupName: string) => {
		setSelectedMarketGroup(marketGroupName);
	}, []);

	const gridOffersWidget = () => {
		return (
			<ClientOnly>
				{() => (
					<OffersWidget
						autohideTerms=""
						className="grid"
						geotargeting=""
						inheritFontFamily=""
						offerLimit="3"
						theme="ocus"
						variation="horizontal-slim"
						vcId="4yyFzILMXoWgjWP0AKAaoV"
					/>
				)}
			</ClientOnly>
		);
	};

	const renderSubeventMarkets = () => {
		if (!oddsGrids) return null;

		const activeSubeventMarkets = marketGroups[selectedMarketGroup];

		const { bookmakers } = siteConfig;

		return (
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					margin: '0 5px',
				}}
			>
				{activeSubeventMarkets
					.filter(market => oddsGrids[market.ocMarketId])
					.map((market, idx) => (
						<Fragment key={market.marketDisplayName}>
							<OddsGrid
								bestOddsStartExpanded
								bookmakersConfig={bookmakers}
								countryCode={geoLocationInfo.countryCode}
								dataApiEndpoint="/api"
								dehydratedState={
									oddsGrids[market.ocMarketId] as DehydratedState
								}
								deviceType={deviceType}
								gridStartsExpanded={false}
								marketId={market.ocMarketId}
								marketIndex={idx}
								marketName={market.marketDisplayName}
								oddsFormat={oddsFormat}
								repub={repub}
								subdivision={geoLocationInfo.subdivisionCode}
								subeventType={market.subeventType}
							/>
							{idx === 0 ? gridOffersWidget() : null}
						</Fragment>
					))}
			</div>
		);
	};

	const renderActiveContent = () => {
		if (selectedTab.name === 'Best Odds') {
			return (
				<>
					<Filters
						handleMarketChange={handleMarketChange}
						marketGroupNames={marketGroupNames}
						repub={repub}
						selectedMarketGroup={selectedMarketGroup}
					/>

					<div className="ogm-subevent-content">{renderSubeventMarkets()}</div>
				</>
			);
		}

		if (selectedTab.name === 'Offers') {
			return (
				<OffersWidget
					DISABLE_OFFER_WIDGETS={DISABLE_OFFER_WIDGETS}
					className="offersTab"
					geotargeting=""
					inheritFontFamily=""
					theme="ocus"
					variation="grouped-offers"
					vcId="3ziiRZwMimMl8tfN10C8ea"
					visibleOffersCount="99"
				/>
			);
		}
	};

	return (
		// Temp solution to add nav/footer directly to route until
		// grid page added to dynamic route
		<StandardLayout {...rootLoaderData} {...data}>
			<div
				style={{
					backgroundColor: 'var(--ogm-ghostwhite)',
					paddingBottom: '30px',
				}}
			>
				{/* NOTE: Generating this element here until Remix v2_meta is fixed */}
				{createSubeventJsonLd({
					locationName: cardMapping.cardTitle,
					name: mapping.displayName,
					startDate: mapping.startDate,
					url: url.toString(),
				})}
				{renderSubeventHeader()}
				<Tabs
					handleTabChange={handleTabChange}
					repub={repub}
					selectedTab={selectedTab}
					tabElements={tabElements}
				/>

				{renderActiveContent()}
			</div>
			<Breadcrumbs />
		</StandardLayout>
	);
}
